/**
 * PrimeArtifactGenerationWizard Component
 * 
 * A step-by-step wizard for complete artifact setup:
 * 1. Apply State Template: Apply template to get base properties
 * 2. Add Additional Properties: Select additional global properties if needed
 * 3. Generate States: Auto-generate states from property state_actions
 * 4. Generate Transitions: Auto-generate transitions from states
 * 5. Generate Processes: Auto-generate processes for transitions
 * 6. Review & Complete: Show completeness summary
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  Steps,
  Button,
  Card,
  Alert,
  Space,
  Typography,
  Spin,
  Result,
  Tag,
  Tooltip,
  Progress,
  Descriptions,
  message
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  ThunderboltOutlined,
  PropertySafetyOutlined,
  BranchesOutlined,
  SwapOutlined,
  SettingOutlined,
  CheckOutlined
} from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import { GlobalPropertySelector } from '../StateTemplates/GlobalPropertySelector';

const { Title, Text, Paragraph } = Typography;

interface WizardStepResult {
  success: boolean;
  count: number;
  skipped: number;
  message?: string;
}

// Interfaces for generate-from-global API (per GLOBAL_TEMPLATE_APPLICATION_GUIDE.md)
interface GenerateFromGlobalOptions {
  template_id?: string;
  apply_properties?: boolean;
  generate_states?: boolean;
  generate_transitions?: boolean;
  generate_processes?: boolean;
  validate_completeness?: boolean;
  update_workflow_status?: boolean;
  auto_approve?: boolean;
}

interface ApplyPropertiesResult {
  properties_created: number;
  properties_skipped: number;
  state_mappings_created: number;
  errors: string[];
}

interface GenerateStatesResult {
  derived_states: {
    states_created: number;
    states: Array<{
      id: string;
      name: string;
      state_type: string;
    }>;
  };
  creation_state?: {
    id: string;
    name: string;
    exists: boolean;
  };
}

interface GenerateTransitionsResult {
  total_created: number;
  creation_transitions: number;
  material_transitions: number;
  processitem_transitions: number;
  resource_transitions: number;
  transitions: Array<{
    id: string;
    transition_name: string;
    transition_type: string;
  }>;
}

interface GenerateProcessesResult {
  processes_created: number;
  processes_assigned: number;
  processes: Array<{
    id: string;
    name: string;
    transition_id: string;
  }>;
}

interface ValidationResult {
  validation: {
    valid: boolean;
    completeness_percentage: number;
    categories: {
      details: { valid: boolean; percentage: number };
      properties: { valid: boolean; percentage: number };
      states: { valid: boolean; percentage: number };
      transitions: { valid: boolean; percentage: number };
      processes: { valid: boolean; percentage: number };
    };
    issues: string[];
  };
  workflow_status_updated: boolean;
  development_status: string;
}

interface GenerateFromGlobalResponse {
  success: boolean;
  artifact_id: string;
  steps: {
    apply_properties?: ApplyPropertiesResult;
    generate_states?: GenerateStatesResult;
    generate_transitions?: GenerateTransitionsResult;
    generate_processes?: GenerateProcessesResult;
    validation?: ValidationResult;
  };
  completeness?: number;
  is_valid?: boolean;
}

interface PrimeArtifactGenerationWizardProps {
  visible: boolean;
  artifactId: string;
  artifactName: string;
  hasStateTemplate?: boolean;
  onClose: () => void;
  onComplete: () => void;
}

export const PrimeArtifactGenerationWizard: React.FC<PrimeArtifactGenerationWizardProps> = ({
  visible,
  artifactId,
  artifactName,
  hasStateTemplate: propHasStateTemplate,
  onClose,
  onComplete
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stepResults, setStepResults] = useState<Record<number, WizardStepResult>>({});
  const [showPropertySelector, setShowPropertySelector] = useState(false);
  const [existingProperties, setExistingProperties] = useState<string[]>([]);
  const [completenessData, setCompletenessData] = useState<any>(null);
  const [hasStateTemplate, setHasStateTemplate] = useState(propHasStateTemplate || false);
  const [artifactStateTemplateId, setArtifactStateTemplateId] = useState<string | null>(null);

  // Fetch artifact data to check for state_template_id when wizard opens
  useEffect(() => {
    const fetchArtifactData = async () => {
      if (visible) {
        try {
          // Fetch artifact to check for state_template_id
          const artifactResponse = await apiClient.get(`/prime-artifacts/${artifactId}`);
          const artifact = artifactResponse.data;
          const stateTemplateId = artifact?.state_template_id;
          
          console.log('[PrimeArtifactGenerationWizard] Fetched artifact data:', {
            artifactId,
            state_template_id: stateTemplateId,
            propHasStateTemplate,
            willUseStateTemplate: !!stateTemplateId
          });
          
          // Use state_template_id from artifact if prop is not provided
          const shouldUseStateTemplate = propHasStateTemplate !== undefined 
            ? propHasStateTemplate 
            : !!stateTemplateId;
          
          setHasStateTemplate(shouldUseStateTemplate);
          setArtifactStateTemplateId(stateTemplateId || null);
          
          // Also fetch existing properties
          const propsResponse = await apiClient.get(`/prime-artifacts/${artifactId}/properties`);
          const properties = Array.isArray(propsResponse.data) ? propsResponse.data : [];
          setExistingProperties(properties.map((p: any) => p.property_key));
        } catch (error) {
          console.error('[PrimeArtifactGenerationWizard] Error fetching artifact data:', error);
          setHasStateTemplate(propHasStateTemplate || false);
          setArtifactStateTemplateId(null);
          setExistingProperties([]);
        }
      }
    };
    void fetchArtifactData();
  }, [visible, artifactId, propHasStateTemplate]);

  // Fetch completeness when reaching the final step
  useEffect(() => {
    const fetchCompleteness = async () => {
      if (currentStep === 5) {
        try {
          const response = await apiClient.get(`/prime-artifacts/${artifactId}/completeness/summary`);
          setCompletenessData(response.data);
        } catch (error) {
          console.error('Error fetching completeness:', error);
        }
      }
    };
    void fetchCompleteness();
  }, [currentStep, artifactId]);

  // Reset state when wizard opens/closes
  useEffect(() => {
    if (visible) {
      setCurrentStep(0);
      setStepResults({});
      setCompletenessData(null);
    }
  }, [visible]);

  // Step 0: Apply Type Template - Uses generate-from-global endpoint
  // This properly applies properties from state template's property_mappings
  const handleApplyTemplate = useCallback(async () => {
    setLoading(true);
    try {
      // Build request options for generate-from-global endpoint
      // Per GLOBAL_TEMPLATE_APPLICATION_GUIDE.md
      const options: GenerateFromGlobalOptions = {
        apply_properties: true,      // Apply properties from template
        generate_states: false,      // States will be generated in later step
        generate_transitions: false, // Transitions will be generated in later step
        generate_processes: false,   // Processes will be generated in later step
        validate_completeness: false,
        update_workflow_status: false
      };
      
      // If we have a state template, include its ID to apply properties from its property_mappings
      if (hasStateTemplate && artifactStateTemplateId) {
        options.template_id = artifactStateTemplateId;
      }
      
      console.log('[PrimeArtifactGenerationWizard] Applying template via generate-from-global:', {
        artifactId,
        hasStateTemplate,
        templateId: options.template_id,
        options
      });
      
      const response = await apiClient.post<GenerateFromGlobalResponse>(
        `/prime-artifacts/${artifactId}/generate-from-global`,
        options
      );
      const result = response.data;
      
      console.log('[PrimeArtifactGenerationWizard] Generate-from-global response:', result);
      
      // Extract properties result from the steps structure
      const applyPropertiesResult = result.steps?.apply_properties;
      const propertiesCount = applyPropertiesResult?.properties_created ?? 0;
      const skippedCount = applyPropertiesResult?.properties_skipped ?? 0;
      const stateMappingsCount = applyPropertiesResult?.state_mappings_created ?? 0;
      const errors = applyPropertiesResult?.errors ?? [];
      
      // Check for errors in property application
      if (errors.length > 0) {
        console.warn('[PrimeArtifactGenerationWizard] Property application errors:', errors);
      }
      
      // Build success message
      let successMessage = '';
      if (propertiesCount > 0 || skippedCount > 0) {
        successMessage = `Applied ${propertiesCount} properties`;
        if (skippedCount > 0) {
          successMessage += ` (${skippedCount} skipped)`;
        }
        if (stateMappingsCount > 0) {
          successMessage += `, ${stateMappingsCount} state mappings`;
        }
      } else {
        successMessage = 'No properties found in template. Check that the template has property mappings defined.';
      }
      
      setStepResults(prev => ({
        ...prev,
        0: {
          success: result.success && errors.length === 0,
          count: propertiesCount,
          skipped: skippedCount,
          message: errors.length > 0 
            ? `Applied with ${errors.length} error(s): ${errors.slice(0, 2).join(', ')}${errors.length > 2 ? '...' : ''}`
            : successMessage
        }
      }));
      
      // Refresh existing properties list
      const propsResponse = await apiClient.get(`/prime-artifacts/${artifactId}/properties`);
      const properties = Array.isArray(propsResponse.data) ? propsResponse.data : [];
      setExistingProperties(properties.map((p: any) => p.property_key));
      
      // Notify parent to refresh properties display
      onComplete();
      
      if (result.success) {
        message.success(propertiesCount > 0 
          ? `Applied ${propertiesCount} properties from template` 
          : 'Template applied');
        setCurrentStep(1);
      } else {
        message.warning('Template applied with issues');
        setCurrentStep(1); // Still advance to next step
      }
    } catch (error: any) {
      console.error('[PrimeArtifactGenerationWizard] Error applying template:', error);
      
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to apply template';
      
      setStepResults(prev => ({
        ...prev,
        0: {
          success: false,
          count: 0,
          skipped: 0,
          message: errorMessage
        }
      }));
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [artifactId, hasStateTemplate, artifactStateTemplateId]);

  // Step 1: Add Additional Properties
  const handleAddAdditionalProperties = useCallback(async (propertyKeys: string[]) => {
    setLoading(true);
    try {
      let addedCount = 0;
      let failedCount = 0;
      const errors: string[] = [];
      
      for (const propertyKey of propertyKeys) {
        try {
          await apiClient.post(`/prime-artifacts/${artifactId}/properties`, {
            property_key: propertyKey,
            property_type: 'string', // Will be overwritten from global
            state_mappings: [],
            referenced_artifacts: []
          });
          addedCount++;
        } catch (e: any) {
          failedCount++;
          const errorMsg = e.response?.data?.detail || e.message || 'Unknown error';
          errors.push(`${propertyKey}: ${errorMsg}`);
          console.error(`[PrimeArtifactGenerationWizard] Failed to add property ${propertyKey}:`, errorMsg);
        }
      }
      
      // Build message with error details if any
      let messageText = `Added ${addedCount} additional properties`;
      if (failedCount > 0) {
        messageText += ` (${failedCount} failed)`;
        if (errors.length > 0 && errors.length <= 3) {
          messageText += `: ${errors.join(', ')}`;
        } else if (errors.length > 3) {
          messageText += `: ${errors.slice(0, 2).join(', ')} and ${errors.length - 2} more`;
        }
      }
      
      setStepResults(prev => ({
        ...prev,
        1: {
          success: addedCount > 0,
          count: addedCount,
          skipped: failedCount,
          message: messageText
        }
      }));
      
      // Refresh existing properties
      const propsResponse = await apiClient.get(`/prime-artifacts/${artifactId}/properties`);
      const properties = Array.isArray(propsResponse.data) ? propsResponse.data : [];
      setExistingProperties(properties.map((p: any) => p.property_key));
      
      // Notify parent to refresh properties display
      onComplete();
      
      setShowPropertySelector(false);
      
      if (addedCount > 0 && failedCount === 0) {
        message.success(`Added ${addedCount} properties`);
      } else if (addedCount > 0 && failedCount > 0) {
        message.warning(`Added ${addedCount} properties, but ${failedCount} failed`);
      } else {
        message.error(`Failed to add properties: ${errors.slice(0, 2).join(', ')}${errors.length > 2 ? '...' : ''}`);
      }
    } catch (error: any) {
      console.error('[PrimeArtifactGenerationWizard] Error in handleAddAdditionalProperties:', error);
      message.error('Failed to add properties: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  }, [artifactId, onComplete]);

  // Step 2: Generate States
  const handleGenerateStates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.post(`/prime-artifacts/${artifactId}/generate/states`);
      const result = response.data;
      
      setStepResults(prev => ({
        ...prev,
        2: {
          success: true,
          count: result.states_created || 0,
          skipped: result.states_skipped || 0,
          message: `Generated ${result.states_created || 0} states`
        }
      }));
      
      message.success(`Generated ${result.states_created || 0} states`);
      setCurrentStep(3);
    } catch (error: any) {
      setStepResults(prev => ({
        ...prev,
        2: {
          success: false,
          count: 0,
          skipped: 0,
          message: error.response?.data?.detail || 'Failed to generate states'
        }
      }));
      message.error(error.response?.data?.detail || 'Failed to generate states');
    } finally {
      setLoading(false);
    }
  }, [artifactId]);

  // Step 3: Generate Transitions
  const handleGenerateTransitions = useCallback(async () => {
    setLoading(true);
    try {
      console.log(`[PrimeArtifactGenerationWizard] Generating transitions for artifact: ${artifactId}`);
      const response = await apiClient.post(`/prime-artifacts/${artifactId}/generate/transitions`);
      const result = response.data;
      
      console.log(`[PrimeArtifactGenerationWizard] Transitions generation response:`, result);
      
      setStepResults(prev => ({
        ...prev,
        3: {
          success: true,
          count: result.total_created || result.transitions_created || 0,
          skipped: result.transitions_skipped || 0,
          message: `Generated ${result.total_created || result.transitions_created || 0} transitions`
        }
      }));
      
      message.success(`Generated ${result.total_created || result.transitions_created || 0} transitions`);
      setCurrentStep(4);
    } catch (error: any) {
      console.error('[PrimeArtifactGenerationWizard] Error generating transitions:', error);
      console.error('[PrimeArtifactGenerationWizard] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      
      const errorDetail = error.response?.data?.detail || error.message || 'Failed to generate transitions';
      
      setStepResults(prev => ({
        ...prev,
        3: {
          success: false,
          count: 0,
          skipped: 0,
          message: errorDetail
        }
      }));
      
      // Show more detailed error message
      message.error(`Failed to generate transitions: ${errorDetail}`);
    } finally {
      setLoading(false);
    }
  }, [artifactId]);

  // Step 4: Generate Processes
  const handleGenerateProcesses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.post(`/prime-artifacts/${artifactId}/generate/processes`);
      const result = response.data;
      
      setStepResults(prev => ({
        ...prev,
        4: {
          success: true,
          count: result.processes_created || 0,
          skipped: result.processes_skipped || 0,
          message: `Generated ${result.processes_created || 0} processes`
        }
      }));
      
      message.success(`Generated ${result.processes_created || 0} processes`);
      setCurrentStep(5);
    } catch (error: any) {
      setStepResults(prev => ({
        ...prev,
        4: {
          success: false,
          count: 0,
          skipped: 0,
          message: error.response?.data?.detail || 'Failed to generate processes'
        }
      }));
      message.error(error.response?.data?.detail || 'Failed to generate processes');
    } finally {
      setLoading(false);
    }
  }, [artifactId]);

  // Run all steps at once using unified generate-from-global endpoint
  const handleRunAll = useCallback(async () => {
    setLoading(true);
    try {
      // Build options per GLOBAL_TEMPLATE_APPLICATION_GUIDE.md
      const options: GenerateFromGlobalOptions = {
        apply_properties: true,
        generate_states: true,
        generate_transitions: true,
        generate_processes: true,
        validate_completeness: true,
        update_workflow_status: true
      };
      
      // Include template_id if artifact has a state template
      if (hasStateTemplate && artifactStateTemplateId) {
        options.template_id = artifactStateTemplateId;
      }
      
      console.log('[PrimeArtifactGenerationWizard] Running full generation:', { artifactId, options });
      
      const response = await apiClient.post<GenerateFromGlobalResponse>(
        `/prime-artifacts/${artifactId}/generate-from-global`,
        options
      );
      const result = response.data;
      
      console.log('[PrimeArtifactGenerationWizard] Full generation response:', result);
      
      // Extract results from the steps structure per documentation
      const propsResult = result.steps?.apply_properties;
      const statesResult = result.steps?.generate_states;
      const transitionsResult = result.steps?.generate_transitions;
      const processesResult = result.steps?.generate_processes;
      
      // Calculate states count from derived_states
      const statesCreated = statesResult?.derived_states?.states_created ?? 0;
      const hasCreationState = statesResult?.creation_state?.id ? 1 : 0;
      const totalStates = statesCreated + hasCreationState;
      
      // Set results for all steps
      setStepResults({
        0: { 
          success: true, 
          count: propsResult?.properties_created ?? 0, 
          skipped: propsResult?.properties_skipped ?? 0, 
          message: `${propsResult?.properties_created ?? 0} properties applied` 
        },
        1: { 
          success: true, 
          count: 0, 
          skipped: 0, 
          message: 'Included in full generation' 
        },
        2: { 
          success: true, 
          count: totalStates, 
          skipped: 0, 
          message: `${totalStates} states generated` 
        },
        3: { 
          success: true, 
          count: transitionsResult?.total_created ?? 0, 
          skipped: 0, 
          message: `${transitionsResult?.total_created ?? 0} transitions generated` 
        },
        4: { 
          success: true, 
          count: processesResult?.processes_created ?? 0, 
          skipped: 0, 
          message: `${processesResult?.processes_created ?? 0} processes generated` 
        }
      });
      
      // Update existing properties list
      const propsResponse = await apiClient.get(`/prime-artifacts/${artifactId}/properties`);
      const properties = Array.isArray(propsResponse.data) ? propsResponse.data : [];
      setExistingProperties(properties.map((p: any) => p.property_key));
      
      // Notify parent to refresh properties display
      onComplete();
      
      message.success('Full generation completed successfully');
      setCurrentStep(5);
    } catch (error: any) {
      console.error('[PrimeArtifactGenerationWizard] Full generation error:', error);
      message.error(error.response?.data?.detail || 'Failed to run full generation');
    } finally {
      setLoading(false);
    }
  }, [artifactId, hasStateTemplate, artifactStateTemplateId]);

  // Step icon based on status
  const getStepIcon = (stepIndex: number) => {
    if (loading && currentStep === stepIndex) {
      return <LoadingOutlined />;
    }
    if (stepResults[stepIndex]?.success) {
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    }
    if (stepResults[stepIndex] && !stepResults[stepIndex].success) {
      return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
    }
    
    const icons = [
      <PropertySafetyOutlined key="props" />,
      <PropertySafetyOutlined key="addprops" />,
      <BranchesOutlined key="states" />,
      <SwapOutlined key="trans" />,
      <SettingOutlined key="proc" />,
      <CheckOutlined key="complete" />
    ];
    return icons[stepIndex];
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Title level={4}>Apply State Template</Title>
              <Paragraph>
                Apply the {hasStateTemplate ? 'state template' : 'artifact type template'} to get base properties 
                for this prime artifact. This will inherit property definitions from global properties.
              </Paragraph>
              <Alert
                message="Properties will be inherited from global definitions"
                description="Property type, validation rules, and input source come from the global property. You can customize only the artifact-specific overrides."
                type="info"
                showIcon
              />
              {stepResults[0] && (
                <Alert
                  message={stepResults[0].success ? 'Template Applied' : 'Error'}
                  description={stepResults[0].message}
                  type={stepResults[0].success ? 'success' : 'error'}
                  showIcon
                />
              )}
              <Space>
                <Button 
                  type="primary" 
                  onClick={handleApplyTemplate}
                  loading={loading}
                  icon={<ThunderboltOutlined />}
                >
                  Apply {hasStateTemplate ? 'State Template' : 'Type Template'}
                </Button>
                <Button onClick={() => setCurrentStep(1)}>
                  Skip (already applied)
                </Button>
              </Space>
            </Space>
          </Card>
        );
      
      case 1:
        return (
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Title level={4}>Add Additional Properties</Title>
              <Paragraph>
                Select additional global properties if you need more than what's included in the template.
                All properties must be based on global property definitions.
              </Paragraph>
              <Alert
                message={`Currently ${existingProperties.length} properties defined`}
                type="info"
                showIcon
              />
              {stepResults[1] && stepResults[1].count > 0 && (
                <Alert
                  message={`Added ${stepResults[1].count} properties`}
                  type="success"
                  showIcon
                />
              )}
              <Space>
                <Button 
                  type="primary" 
                  onClick={() => setShowPropertySelector(true)}
                  icon={<PropertySafetyOutlined />}
                >
                  Add Properties from Global
                </Button>
                <Button onClick={() => setCurrentStep(2)}>
                  Continue (skip additional properties)
                </Button>
              </Space>
            </Space>
            <GlobalPropertySelector
              visible={showPropertySelector}
              artifactId={artifactId}
              onCancel={() => setShowPropertySelector(false)}
              onSelect={handleAddAdditionalProperties}
              excludeProperties={existingProperties}
              context="artifact"
            />
          </Card>
        );
      
      case 2:
        return (
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Title level={4}>Generate States</Title>
              <Paragraph>
                Generate states from property <code>state_actions</code> definitions. 
                This analyzes all properties and creates states where properties are created or updated.
              </Paragraph>
              {stepResults[2] && (
                <Alert
                  message={stepResults[2].success ? 'States Generated' : 'Error'}
                  description={stepResults[2].message}
                  type={stepResults[2].success ? 'success' : 'error'}
                  showIcon
                />
              )}
              <Space>
                <Button 
                  type="primary" 
                  onClick={handleGenerateStates}
                  loading={loading}
                  icon={<BranchesOutlined />}
                >
                  Generate States
                </Button>
                <Button onClick={() => setCurrentStep(3)}>
                  Skip
                </Button>
              </Space>
            </Space>
          </Card>
        );
      
      case 3:
        return (
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Title level={4}>Generate Transitions</Title>
              <Paragraph>
                Generate transitions between states based on state relationships and property flow directions.
                This creates the connections between states.
              </Paragraph>
              {stepResults[3] && (
                <Alert
                  message={stepResults[3].success ? 'Transitions Generated' : 'Error'}
                  description={stepResults[3].message}
                  type={stepResults[3].success ? 'success' : 'error'}
                  showIcon
                />
              )}
              <Space>
                <Button 
                  type="primary" 
                  onClick={handleGenerateTransitions}
                  loading={loading}
                  icon={<SwapOutlined />}
                >
                  Generate Transitions
                </Button>
                <Button onClick={() => setCurrentStep(4)}>
                  Skip
                </Button>
              </Space>
            </Space>
          </Card>
        );
      
      case 4:
        return (
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Title level={4}>Generate Processes</Title>
              <Paragraph>
                Generate processes for transitions. Processes define the business logic 
                that executes when transitioning between states.
              </Paragraph>
              {stepResults[4] && (
                <Alert
                  message={stepResults[4].success ? 'Processes Generated' : 'Error'}
                  description={stepResults[4].message}
                  type={stepResults[4].success ? 'success' : 'error'}
                  showIcon
                />
              )}
              <Space>
                <Button 
                  type="primary" 
                  onClick={handleGenerateProcesses}
                  loading={loading}
                  icon={<SettingOutlined />}
                >
                  Generate Processes
                </Button>
                <Button onClick={() => setCurrentStep(5)}>
                  Skip
                </Button>
              </Space>
            </Space>
          </Card>
        );
      
      case 5:
        return (
          <Card>
            <Result
              status="success"
              title="Generation Complete!"
              subTitle={`Prime artifact "${artifactName}" has been set up.`}
            >
              <Descriptions column={2} bordered size="small">
                <Descriptions.Item label="Properties">
                  <Tag color="blue">{stepResults[0]?.count || 0} created</Tag>
                  {stepResults[0]?.skipped > 0 && <Tag>{stepResults[0].skipped} skipped</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Additional Properties">
                  <Tag color="blue">{stepResults[1]?.count || 0} added</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="States">
                  <Tag color="green">{stepResults[2]?.count || 0} created</Tag>
                  {stepResults[2]?.skipped > 0 && <Tag>{stepResults[2].skipped} skipped</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Transitions">
                  <Tag color="orange">{stepResults[3]?.count || 0} created</Tag>
                  {stepResults[3]?.skipped > 0 && <Tag>{stepResults[3].skipped} skipped</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Processes">
                  <Tag color="purple">{stepResults[4]?.count || 0} created</Tag>
                  {stepResults[4]?.skipped > 0 && <Tag>{stepResults[4].skipped} skipped</Tag>}
                </Descriptions.Item>
                {completenessData && (
                  <Descriptions.Item label="Completeness">
                    <Progress 
                      percent={completenessData.percentage || 0} 
                      size="small" 
                      status={completenessData.percentage === 100 ? 'success' : 'active'}
                    />
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Result>
          </Card>
        );
      
      default:
        return null;
    }
  };

  return (
    <Modal
      title={
        <Space>
          <ThunderboltOutlined />
          <span>Prime Artifact Generation Wizard</span>
          <Tag color="blue">{artifactName}</Tag>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={
        <Space>
          <Button onClick={onClose}>
            Cancel
          </Button>
          {currentStep < 5 && (
            <Tooltip title="Run all steps at once">
              <Button 
                type="primary"
                onClick={handleRunAll}
                loading={loading}
                icon={<ThunderboltOutlined />}
              >
                Run All Steps
              </Button>
            </Tooltip>
          )}
          {currentStep === 5 && (
            <Button type="primary" onClick={() => { onComplete(); onClose(); }}>
              Done
            </Button>
          )}
        </Space>
      }
    >
      <Steps 
        current={currentStep}
        size="small"
        style={{ marginBottom: 24 }}
        items={[
          { title: 'Template', icon: getStepIcon(0) },
          { title: 'Properties', icon: getStepIcon(1) },
          { title: 'States', icon: getStepIcon(2) },
          { title: 'Transitions', icon: getStepIcon(3) },
          { title: 'Processes', icon: getStepIcon(4) },
          { title: 'Complete', icon: getStepIcon(5) }
        ]}
      />
      
      {renderStepContent()}
    </Modal>
  );
};

export default PrimeArtifactGenerationWizard;
